import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client';

const useSubmitWithOfflineSupport = (mutation, offlineDBHandler = null) => {

    const [mutateFunction, { data: gqlData, loading: gqlLoading, error: gqlError }] = useMutation(mutation);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const [errorList, setErrorList] = useState();

    // Bind gql changes to the component states
    useEffect(() => { setData(gqlData) }, [gqlData]);
    useEffect(() => { setLoading(gqlLoading) }, [gqlLoading]);
    useEffect(() => {
        if (gqlError?.networkError?.statusCode === 401) {
            setError('You have been logged-in for too long. Please log back in.');
        }
        else if (
            gqlError?.message === 'Failed to fetch' ||
            gqlError?.graphQLErrors?.[0]?.extensions?.code === 'INTERNAL_SERVER_ERROR' ||
            gqlError?.graphQLErrors?.[0]?.extensions?.code === 'UNEXPECTED' ||
            error?.graphQLErrors?.[0]?.extensions?.code === 'BAD_USER_INPUT'
        ) {
            setError('Problem: An unexpected issue has occurred');
        }
        else if (gqlError?.graphQLErrors?.[0]?.extensions?.code === 'FORBIDDEN') {
            setError('You cannot do this with your account.');
        }
        else if (gqlError?.graphQLErrors?.[0]?.extensions?.code === 'VALIDATION_FAILED') {
            gqlError?.graphQLErrors?.[0]?.extensions?.errors &&
                setErrorList(gqlError?.graphQLErrors?.[0]?.extensions?.errors);
        }
        else if (gqlError?.graphQLErrors?.[0]?.extensions?.code === 'APPLICATION_FAILED') {
            const applicationErrors = gqlError?.graphQLErrors?.[0]?.extensions?.errors;
            // the service tells us which field caused the failure (eg: a duplicate ear tag),
            // so surface those on the fields and only fall back to a generic message when it doesn't.
            if (Array.isArray(applicationErrors) && applicationErrors.some(i => i?.field)) {
                setErrorList(applicationErrors);
            }
            else {
                setError('an unexpected issue has occured');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gqlError]);

    // eslint-disable-next-line consistent-return
    const submitFunction = async (dataObject) => {
        setLoading(true);
        if (window.navigator.onLine) {

            // online, save to the server
            try {
                const result = await mutateFunction({ variables: { ...dataObject } });
                setLoading(false);
                return result;
            }
            catch (err) {
                if (err?.networkError?.statusCode === 401) {
                    setError('You have been logged-in for too long. Please log back in.');
                }
                else if (
                    err?.message === 'Failed to fetch' ||
                    err?.graphQLErrors?.[0]?.extensions?.code === 'INTERNAL_SERVER_ERROR' ||
                    err?.graphQLErrors?.[0]?.extensions?.code === 'UNEXPECTED' ||
                    err?.graphQLErrors?.[0]?.extensions?.code === 'BAD_USER_INPUT'
                ) {
                    setError('Problem: An unexpected issue has occurred');
                }
                else if (err?.graphQLErrors?.[0]?.extensions?.code === 'FORBIDDEN') {
                    setError('You cannot do this with your account.');
                }
                else if (err?.graphQLErrors?.[0]?.extensions?.code === 'VALIDATION_FAILED') {
                    err?.graphQLErrors?.[0]?.extensions?.errors &&
                        setErrorList(err?.graphQLErrors?.[0]?.extensions?.errors);
                }
                else if (err?.graphQLErrors?.[0]?.extensions?.code === 'APPLICATION_FAILED') {
                    const applicationErrors = err?.graphQLErrors?.[0]?.extensions?.errors;
                    // the service tells us which field caused the failure (eg: a duplicate ear tag),
                    // so surface those on the fields and only fall back to a generic message when it doesn't.
                    if (Array.isArray(applicationErrors) && applicationErrors.some(i => i?.field)) {
                        setErrorList(applicationErrors);
                    }
                    else {
                        setError('an unexpected issue has occured');
                    }
                }
                setLoading(false);
            }
        }
        else if (offlineDBHandler) {
            // offline, save to the offline database via the provided handler
            try {
                const offlineSaveResult = await offlineDBHandler(dataObject);
                if (offlineSaveResult?.success) {
                    offlineSaveResult?.data && setData(offlineSaveResult.data);
                    setLoading(false);
                    return offlineSaveResult;
                }
                else if (offlineSaveResult && !offlineSaveResult.success && offlineSaveResult.code === 403) {
                    setError('Error: Permission Denied!');
                }
                else {
                    setError('An error occured while trying to save offline for syncing later!');
                }
            }
            catch (err) {
                setError('An error occured while trying to save offline for syncing later!');
            }
            // offline, handler not provided, return 'offline' error
            setLoading(false);
        }
        else {
            // offline, handler not provided, return 'offline' error
            setError('This action is not supported in offline mode!');
            setLoading(false);
        }
    }

    return [submitFunction, {
        data, loading, error, errorList
    }];
    // errorList will exist when there is an array of errors (for validation and field related errors).
}

export default useSubmitWithOfflineSupport;
